import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Printer, AlertCircle, Copy, Building2, Check, Smartphone,
    Calendar, ExternalLink, FileText, CalendarClock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public, read-only client view of an invoice.
 *
 * Payment methods available to the end-client (in order of prominence):
 *   1. BONIFICO BANCARIO (primary — IBAN + causale precompilata)
 *   2. PayPal (paypal.me link, funds → artisan)
 *   3. Satispay (link, funds → artisan)
 *   4. Rateizzazione (email placeholder)
 *
 * Card payments are INTENTIONALLY disabled on customer invoices — the platform
 * cannot legally be the merchant of record for funds owed to the artisan, and
 * routing card payments through the platform's Stripe account would create a
 * misuse-of-funds problem. See backend/routers/payments.py::create_fattura_checkout
 * for the full rationale. Card payments remain enabled only for platform
 * subscriptions.
 */
export default function PublicFattura() {
    const { t } = useTranslation();
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await axios.get(`${BACKEND_URL}/api/public/fatture/${token}`);
                if (active) setData(res.data);
            } catch (e) {
                if (active) setError(e.response?.status === 404 ? 'not_found' : 'error');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    const copyIban = async () => {
        try {
            await navigator.clipboard.writeText(data.iban);
            toast.success(t('pages.pubproforma_iban_copied') || 'IBAN copiato');
        } catch (_) { toast.error('Errore copia'); }
    };
    const copyAmount = async () => {
        try {
            await navigator.clipboard.writeText(String(data.importo));
            toast.success(t('pages.pubproforma_amount_copied') || 'Importo copiato');
        } catch (_) { toast.error('Errore copia'); }
    };
    const formatDate = (iso) => {
        if (!iso) return '—';
        try { return new Date(String(iso).slice(0, 10)).toLocaleDateString(); }
        catch { return String(iso).slice(0, 10); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] py-8 px-4">
                <div className="max-w-2xl mx-auto space-y-4">
                    <Skeleton className="h-12 w-1/2" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-60 w-full" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
                <Card className="p-8 max-w-md text-center">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-[hsl(0_72%_52%)]" />
                    <h2 className="font-display text-2xl mb-2">{t('pages.pubfattura_not_found') || 'Fattura non trovata'}</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.pubproforma_link_invalid') || 'Il link potrebbe non essere più valido.'}</p>
                </Card>
            </div>
        );
    }

    const isPagata = data.stato === 'pagata';
    const isScaduta = data.stato === 'scaduta';
    const importoFmt = Number(data.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

    return (
        <div className="min-h-screen bg-[hsl(var(--background))] py-6 px-4" data-testid="public-fattura">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl">{t('pages.pubfattura_title') || 'FATTURA'}</h1>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{data.numero}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isPagata ? (
                            <Badge className="bg-[hsl(145_55%_34%)] text-white" data-testid="fattura-badge"><Check className="w-3 h-3 mr-1" /> {t('pages.pubproforma_paid_badge') || 'PAGATA'}</Badge>
                        ) : isScaduta ? (
                            <Badge className="bg-[hsl(0_75%_45%)] text-white" data-testid="fattura-badge">{t('pages.prev_proforma_stato_scaduta') || 'SCADUTA'}</Badge>
                        ) : (
                            <Badge className="bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]" data-testid="fattura-badge">{t('pages.pubproforma_topay_badge') || 'DA PAGARE'}</Badge>
                        )}
                        <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-1 no-print"><Printer className="w-4 h-4" /> {t('common.print') || 'Stampa'}</Button>
                    </div>
                </div>

                {/* Hero importo */}
                <Card className={`p-6 mb-4 ${isPagata ? 'border-[hsl(145_55%_34%)]/40 bg-[hsl(145_45%_98%)]' : isScaduta ? 'border-[hsl(0_75%_45%)]/40 bg-[hsl(0_60%_98%)]' : 'border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5'}`}>
                    <div className="text-center">
                        <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2">{t('pages.pubfattura_amount_label') || 'Importo da pagare'}</div>
                        <div className="font-display text-5xl md:text-6xl tabular-nums" data-testid="fattura-importo-hero">€{importoFmt}</div>
                        {data.data_scadenza && (
                            <div className={`text-sm mt-2 inline-flex items-center gap-1 ${isScaduta ? 'text-[hsl(0_75%_45%)] font-semibold' : 'text-[hsl(var(--muted-foreground))]'}`}>
                                <CalendarClock className="w-3.5 h-3.5" />
                                {t('pages.pubfattura_due_label') || 'Scadenza'}: {formatDate(data.data_scadenza)}
                            </div>
                        )}
                        {!isPagata && (
                            <div className="mt-3">
                                <Button onClick={copyAmount} variant="outline" size="sm" className="gap-1 no-print">
                                    <Copy className="w-3 h-3" /> {t('pages.pubproforma_copy_amount') || 'Copia importo'}
                                </Button>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Cliente + righe sintetiche */}
                <Card className="p-5 mb-4">
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{t('pages.pubproforma_cliente_label') || 'Cliente'}</div>
                    <div className="font-semibold text-lg mb-3">{data.cliente}</div>
                    {Array.isArray(data.righe) && data.righe.length > 0 && (
                        <>
                            <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2 mt-3">{t('pages.pubfattura_lines_label') || 'Dettaglio voci'}</div>
                            <div className="space-y-1 text-sm">
                                {data.righe.slice(0, 8).map((r, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2 py-1 border-b last:border-b-0 border-[hsl(var(--border))]/40">
                                        <span className="truncate flex-1">{r.descrizione || r.description || '—'}</span>
                                        <span className="text-[hsl(var(--muted-foreground))] tabular-nums">{Number(r.quantita || r.qty || 1)} × €{Number(r.prezzo_unitario || r.unit_price || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                ))}
                                {data.righe.length > 8 && (
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] pt-1">+ {data.righe.length - 8} {t('pages.pubfattura_lines_more') || 'altre voci'}</div>
                                )}
                            </div>
                        </>
                    )}
                </Card>

                {/* Paga online (Bonifico is the primary rail on FATTURE — card is disabled
                    because funds would otherwise land on the platform's Stripe account.
                    PayPal/Satispay go directly to the artisan and remain enabled.) */}
                {!isPagata && (data.paypal_me || data.satispay_link || data.rateizza_attivo) && (() => {
                    const commCliente = data.commissioni_a_carico === 'cliente';
                    const commPct = parseFloat(data.commissioni_pct || 0);
                    const importoCarta = commCliente && commPct > 0
                        ? Number(data.importo) * (1 + commPct / 100)
                        : Number(data.importo);
                    const importoCartaFmt = importoCarta.toLocaleString('it-IT', { minimumFractionDigits: 2 });
                    const paypalUrl = data.paypal_me && (data.paypal_me.startsWith('http') ? `${data.paypal_me}/${importoCarta.toFixed(2)}EUR` : `https://paypal.me/${data.paypal_me}/${importoCarta.toFixed(2)}EUR`);
                    return (
                        <Card className="p-5 mb-4 border-[hsl(var(--accent))]/30" data-testid="fattura-alt-payments">
                            <h3 className="font-display text-xl flex items-center gap-2 mb-3 pb-2 border-b border-[hsl(var(--border))]">
                                <Smartphone className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.pubfattura_alt_pay_title') || 'Altri metodi di pagamento'}
                            </h3>
                            <div className="grid gap-2">
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {data.paypal_me && (
                                        <Button asChild className="bg-[#0070BA] hover:bg-[#005a96] text-white h-12 gap-2 font-bold" data-testid="fattura-paypal-button">
                                            <a href={paypalUrl} target="_blank" rel="noopener noreferrer">
                                                <span className="inline-block w-6 h-6 rounded bg-white text-[#0070BA] text-xs font-bold flex items-center justify-center">P</span>
                                                {t('pages.pubproforma_pay_paypal') || 'Paga con PayPal'}
                                                <ExternalLink className="w-3 h-3 ml-1" />
                                            </a>
                                        </Button>
                                    )}
                                    {data.satispay_link && (
                                        <Button asChild className="bg-[#E91E50] hover:bg-[#c1183f] text-white h-12 gap-2 font-bold" data-testid="fattura-satispay-button">
                                            <a href={data.satispay_link} target="_blank" rel="noopener noreferrer">
                                                <Smartphone className="w-4 h-4" />
                                                {t('pages.pubproforma_pay_satispay') || 'Paga con Satispay'}
                                                <ExternalLink className="w-3 h-3 ml-1" />
                                            </a>
                                        </Button>
                                    )}
                                </div>
                                {data.rateizza_attivo && (
                                    <Button asChild variant="outline" className="h-12 gap-2 border-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-semibold" data-testid="fattura-rateizza-button">
                                        <a href={`mailto:?subject=Richiesta rateizzazione ${data.numero}&body=Vorrei informazioni sulla rateizzazione della fattura ${data.numero} per l'importo di €${importoCartaFmt}.`}>
                                            <Calendar className="w-4 h-4" />
                                            {t('pages.pubproforma_pay_rateizza') || 'Rateizza'}
                                            {data.rateizza_info && <span className="text-xs font-normal opacity-70 ml-2 truncate hidden sm:inline">— {data.rateizza_info}</span>}
                                        </a>
                                    </Button>
                                )}
                            </div>
                        </Card>
                    );
                })()}

                {/* Modalità di pagamento: BONIFICO BANCARIO (primary rail for fatture) */}
                {!isPagata && (
                    <Card className="p-5 mb-4 border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/5" data-testid="fattura-bonifico-block">
                        <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-[hsl(var(--accent))] text-white uppercase tracking-wider text-[10px]">{t('pages.pubfattura_bonifico_recommended') || 'Metodo consigliato'}</Badge>
                        </div>
                        <h3 className="font-display text-xl flex items-center gap-2 mb-3 pb-2 border-b border-[hsl(var(--border))]">
                            <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.pubfattura_bonifico_title') || 'Modalità di pagamento: bonifico bancario'}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('pages.pubfattura_bonifico_intestatario') || 'Intestatario'}</div>
                                <div className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" /> {data.beneficiario}</div>
                                {data.piva_beneficiario && <div className="text-xs text-[hsl(var(--muted-foreground))]">P.IVA {data.piva_beneficiario}</div>}
                            </div>
                            {data.iban ? (
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">IBAN</div>
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--muted))] font-mono text-sm tracking-wider break-all">
                                        <span className="flex-1" data-testid="fattura-iban-value">{data.iban}</span>
                                        <Button onClick={copyIban} variant="ghost" size="sm" className="h-8 px-2 no-print" data-testid="fattura-copy-iban"><Copy className="w-3 h-3" /></Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-xs text-[hsl(0_72%_52%)] flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" /> {t('pages.pubfattura_bonifico_iban_missing') || 'IBAN non configurato dal fornitore — contattalo direttamente.'}
                                </div>
                            )}
                            {data.banca_nome && (
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('pages.pubproforma_bank') || 'Banca'}</div>
                                    <div className="text-sm">{data.banca_nome}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('pages.pubfattura_bonifico_causale') || 'Causale (obbligatoria)'}</div>
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--muted))] font-mono text-sm break-all">
                                    <span className="flex-1" data-testid="fattura-causale-value">{t('pages.pubfattura_bonifico_causale_prefix') || 'Fattura'} {data.numero}</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('pages.pubfattura_bonifico_amount') || 'Importo'}</div>
                                <div className="font-mono tabular-nums text-lg">€{importoFmt}</div>
                            </div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] italic pt-2 border-t border-[hsl(var(--border))]/40">
                                {t('pages.pubfattura_bonifico_hint') || "Effettua il bonifico all'IBAN indicato riportando esattamente la causale sopra. Il pagamento verrà registrato entro 1-2 giorni lavorativi dalla ricezione."}
                            </div>
                        </div>
                    </Card>
                )}

                {/* Documento info */}
                <Card className="p-5 mb-4 border-[hsl(var(--border))]">
                    <h3 className="font-display text-sm flex items-center gap-2 mb-2 text-[hsl(var(--muted-foreground))]">
                        <FileText className="w-4 h-4" /> {t('pages.pubfattura_doc_info') || 'Dati documento'}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.pubfattura_doc_date') || 'Data emissione'}</div>
                            <div className="font-mono">{formatDate(data.data_emissione)}</div>
                        </div>
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.pubfattura_doc_imponibile') || 'Imponibile'}</div>
                            <div className="font-mono tabular-nums">€{Number(data.imponibile || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                        </div>
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">IVA</div>
                            <div className="font-mono tabular-nums">€{Number(data.iva_totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                        </div>
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.pubfattura_doc_total') || 'Totale'}</div>
                            <div className="font-display text-base tabular-nums">€{importoFmt}</div>
                        </div>
                    </div>
                </Card>

                {/* Footer */}
                <div className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-6">
                    {t('pages.pubproforma_footer') || 'Powered by'} <span className="font-display font-semibold">CantiereAI</span>
                </div>
            </div>
        </div>
    );
}
